import React, { useState, useEffect, useCallback } from 'react';
import api from '../config';
import {
  GitBranch, Loader2, FileText, ChevronDown, ChevronRight, ArrowRight, Download,
  Ban, PauseCircle, ShieldCheck,
} from 'lucide-react';

/**
 * Deal execution panel: the phase-gate decision tree (Screening → IOI → LOI →
 * Diligence → IC → Closing) plus IOI / LOI / IC-memo generation. Documents are
 * generated strictly from the deal's real data.
 */
// Light letter formatter: turns the model's markdown-ish output into clean
// prose. Headings become bold section titles, **bold** becomes bold, bullets
// become indented lists — no raw markup shown to the user.
function renderDocBody(body) {
  const lines = body.split('\n');
  const out = [];
  let list = [];
  let table = [];
  const flushList = (key) => {
    if (list.length) { out.push(<ul key={key} className="my-1.5 ml-5 list-disc space-y-1">{list}</ul>); list = []; }
  };
  const flushTable = (key) => {
    if (!table.length) return;
    const [head, ...rows] = table;
    out.push(
      <div key={key} className="my-3 overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-navy-900 text-white">
              {head.map((c, i) => <th key={i} className="text-left font-semibold px-3 py-2">{inlineFormat(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className={ri % 2 ? 'bg-slate-50' : 'bg-white'}>
                {head.map((_, ci) => (
                  <td key={ci} className="px-3 py-2 text-slate-700 align-top border-t border-slate-200">
                    {inlineFormat(r[ci] || '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    table = [];
  };
  const splitRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    // pipe tables
    if (line.trim().startsWith('|') && line.split('|').length >= 3) {
      flushList(`l${i}`);
      if (/^\|[\s:|-]+\|$/.test(line.trim())) return; // separator row
      table.push(splitRow(line));
      return;
    }
    if (table.length) flushTable(`t${i}`);
    if (/^#{1,4}\s+/.test(line)) {
      flushList(`l${i}`);
      out.push(
        <div key={i} className="mt-4 mb-1 font-semibold text-slate-900 text-sm tracking-wide">
          {inlineFormat(line.replace(/^#{1,4}\s+/, ''))}
        </div>
      );
    } else if (/^([-*•]|\u2022)\s+/.test(line)) {
      list.push(<li key={i} className="text-sm text-slate-700">{inlineFormat(line.replace(/^([-*•]|\u2022)\s+/, ''))}</li>);
    } else if (line === '' ) {
      flushList(`l${i}`);
      out.push(<div key={i} className="h-2" />);
    } else {
      flushList(`l${i}`);
      out.push(<p key={i} className="text-sm text-slate-700 leading-relaxed my-1">{inlineFormat(line)}</p>);
    }
  });
  flushList('lend');
  flushTable('tend');
  return out;
}

function inlineFormat(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p.replace(/`([^`]+)`/g, '$1')}</span>
  );
}

/* Inline PDF preview of a generated document. Fetches the file with the
   auth token (the raw route is protected), then renders it in an iframe with
   a download button. Falls back to the letter text if no PDF is available. */
function DocPdf({ doc, dealId }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  // One-time review confirmation. A document leaves the firm under the firm's
  // name, so the first download asks the firm to confirm it has read the
  // document and its notice. Recorded once per workspace.
  const [ack, setAck] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [savingAck, setSavingAck] = useState(false);

  useEffect(() => {
    api.get('/api/dd/docs/review-ack')
      .then(({ data }) => setAck(data))
      .catch(() => setAck({ acknowledged: true, notice: '' }));
  }, []);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    if (!doc.pdf_url) { setFetching(false); setFailed(true); return undefined; }
    setFetching(true);
    api.get(doc.pdf_url, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setPdfUrl(objectUrl);
        setFetching(false);
      })
      .catch(() => { if (!cancelled) { setFailed(true); setFetching(false); } });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.pdf_url]);

  const saveDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${dealId}_${doc.kind || 'document'}.pdf`;
    a.click();
  };

  const download = () => {
    if (!pdfUrl) return;
    if (ack && ack.acknowledged === false) {
      setGateOpen(true);
      return;
    }
    saveDownload();
  };

  const confirmReview = async () => {
    setSavingAck(true);
    try {
      const { data } = await api.post('/api/dd/docs/review-ack');
      setAck((prev) => ({ ...(prev || {}), ...data }));
      setGateOpen(false);
      saveDownload();
    } catch {
      // Do not block the firm's own document on a bookkeeping failure: the
      // document is delivered and the confirmation is retried next time.
      setGateOpen(false);
      saveDownload();
    } finally {
      setSavingAck(false);
    }
  };

  if (fetching) {
    return <div className="flex items-center gap-2 text-xs text-slate-400 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading PDF…</div>;
  }
  if (failed || !pdfUrl) {
    return (
      <div className="space-y-2">
        {doc.template_note && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{doc.template_note}</p>
        )}
        <div className="doc-body">{renderDocBody(doc.body || '')}</div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {doc.template_note && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{doc.template_note}</p>
      )}
      <div className="flex justify-end">
        <button onClick={download} className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> Download PDF
        </button>
      </div>
      {gateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/50 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-5 shadow-xl">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
              <div>
                <h4 className="text-sm font-bold text-navy-900">Before this document leaves your firm</h4>
                <p className="mt-1 text-[12px] text-slate-600">
                  This document goes out under your firm&apos;s name. Please read the notice it carries,
                  then confirm you have reviewed the document itself.
                </p>
              </div>
            </div>
            <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11.5px] leading-relaxed text-slate-600">
                {ack?.notice || 'This document was prepared by the firm using Caply. The firm is the author and issuer of this document.'}
              </p>
            </div>
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-[12px] text-slate-700">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300"
              />
              <span>
                I have reviewed this document and its notice. I understand the figures come from what my firm
                supplied and that my firm is responsible for what it sends.
              </span>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => { setGateOpen(false); setChecked(false); }}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReview}
                disabled={!checked || savingAck}
                className="rounded-lg bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
              >
                {savingAck ? 'Saving…' : 'Confirm and download'}
              </button>
            </div>
            <p className="mt-2 text-[10.5px] text-slate-400">
              Asked once per workspace. Everyone on the team can then download without the prompt.
            </p>
          </div>
        </div>
      )}
      <iframe
        title={`PDF preview — ${doc.title || doc.kind}`}
        src={pdfUrl}
        className="w-full rounded-lg border border-slate-200 bg-slate-100"
        style={{ height: 620 }}
      />
    </div>
  );
}

const DRAFT_BUTTONS = [
  ['ioi', 'IOI'],
  ['loi', 'LOI'],
  ['ic_memo', 'IC Memo'],
  ['term_sheet', 'Term Sheet'],
  ['cover_letter', 'Cover Letter'],
];

const VERDICT_STYLES = {
  proceed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  proceed_with_conditions: 'bg-amber-50 text-amber-700 border-amber-200',
  restructure: 'bg-orange-50 text-orange-700 border-orange-200',
  decline: 'bg-rose-50 text-rose-700 border-rose-200',
  insufficient_data: 'bg-slate-50 text-slate-500 border-slate-200',
};

export default function DealDocs({ dealId }) {
  const [phase, setPhase] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [error, setError] = useState('');
  const [rationale, setRationale] = useState('');
  const [openDoc, setOpenDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        api.get(`/api/dd/deals/${dealId}/phase`).catch(() => null),
        api.get(`/api/dd/deals/${dealId}/docs`).catch(() => null),
      ]);
      if (p) setPhase(p.data);
      if (d) setDocs(d.data.docs || []);
      setError('');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const generateDoc = async (kind) => {
    setBusy(true);
    setBusyKind(kind);
    setError('');
    try {
      await api.post(`/api/dd/deals/${dealId}/docs/${kind}`);
      await load();
      setOpenDoc(kind);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate the document.');
    } finally {
      setBusy(false);
      setBusyKind(null);
    }
  };

  const decide = async (decision) => {
    if (!rationale || rationale.trim().length < 3) {
      setError('A rationale is required for every gate decision.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.put(`/api/dd/deals/${dealId}/phase`, { decision, rationale: rationale.trim() });
      setRationale('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not record the decision.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-2 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading deal execution…
      </div>
    );
  }

  const closed = phase?.state === 'closed' || phase?.state === 'passed';
  const hasMemo = docs.some((d) => d.kind === 'ic_memo');

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <GitBranch className="w-5 h-5 text-brand-600" /> Deal Execution
        </div>
        <span className={`text-xs rounded-full px-2.5 py-1 border ${
          phase?.state === 'passed' ? 'bg-rose-50 text-rose-700 border-rose-200'
          : phase?.state === 'closed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
          {phase?.phase_label}
        </span>
      </div>

      {error && <div className="text-xs text-rose-600">{error}</div>}

      {/* Phase progress bar */}
      <div className="flex items-center gap-1">
        {(phase?.phases || []).map((p, i) => (
          <React.Fragment key={p.id}>
            {i > 0 && <ArrowRight className="w-3 h-3 text-slate-300 flex-shrink-0" />}
            <span
              title={p.description}
              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                p.status === 'current' ? 'bg-brand-50 text-brand-700 border border-brand-200 font-semibold'
                : p.status === 'done' ? 'bg-emerald-50 text-emerald-700'
                : 'bg-slate-50 text-slate-400'}`}
            >
              {p.label}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Gate decisions */}
      {!closed && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <div className="text-xs text-slate-500 mb-2">
            Record the next gate decision for{' '}
            <b>{phase?.phase_label}</b> → <b>{phase?.next_phase_label || '—'}</b>
          </div>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Rationale (required) — why is the deal ready to advance, or not?"
            rows={2}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2 bg-white text-slate-800 placeholder-slate-400"
            disabled={busy}
          />
          <div className="flex gap-2">
            <button
              onClick={() => decide('advance')}
              disabled={busy || !phase?.next_phase}
              className="btn-primary text-xs py-2 px-4"
            >
              Advance to {phase?.next_phase_label || '—'}
            </button>
            <button onClick={() => decide('hold')} disabled={busy} className="btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1.5">
              <PauseCircle className="w-3.5 h-3.5" /> Hold
            </button>
            <button onClick={() => decide('pass')} disabled={busy} className="btn-secondary text-xs py-2 px-3 text-rose-600 inline-flex items-center gap-1.5">
              <Ban className="w-3.5 h-3.5" /> Pass on Deal
            </button>
          </div>
        </div>
      )}

      {/* Documents */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <FileText className="w-4 h-4 text-slate-400" /> Deal Documents
          </div>
          <div className="flex flex-wrap gap-2">
            {DRAFT_BUTTONS.map(([kind, label]) => (
              <button
                key={kind}
                onClick={() => generateDoc(kind)}
                disabled={busy}
                className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5"
              >
                {busyKind === kind && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busyKind === kind ? 'Drafting…' : `Draft ${label}`}
              </button>
            ))}
            <button
              onClick={() => generateDoc('board_pack')}
              disabled={busy || !hasMemo}
              title={hasMemo ? 'One PDF for the board: cover letter, IC memo and term sheet' : 'Draft the IC memo first — the pack is assembled from the documents on the deal'}
              className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busyKind === 'board_pack' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {busyKind === 'board_pack' ? 'Assembling…' : 'Build Board Pack'}
            </button>
          </div>
        </div>
        {docs.length === 0 ? (
          <div className="text-xs text-slate-400">
            No documents yet. Caply drafts each one from this deal's real data only — the IC memo adds a computed
            recommendation, valuation multiples, CIM claims vs actuals and ranked risks; the term sheet carries the
            economics from your saved model.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => {
              const open = openDoc === d.kind;
              return (
                <div key={d.id || d.kind} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 text-left"
                    onClick={() => setOpenDoc(open ? null : d.kind)}
                  >
                    <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-2 flex-wrap">
                      {d.title || d.kind}
                      {d.recommendation?.label && (
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 border ${VERDICT_STYLES[d.recommendation.verdict] || VERDICT_STYLES.insufficient_data}`}>
                          {d.recommendation.label}
                        </span>
                      )}
                      {d.kind === 'board_pack' && (d.included || []).length > 0 && (
                        <span className="text-[11px] font-normal text-slate-500">
                          {d.included.length} document{d.included.length === 1 ? '' : 's'}
                          {(d.missing || []).length > 0 && ` · ${d.missing.length} not yet drafted`}
                        </span>
                      )}
                      {d.template_used && (
                        <span className="ml-2 text-[11px] font-normal text-brand-700 bg-brand-50 border border-brand-200 rounded-full px-2 py-0.5" title={`Generated with the firm's branded template: ${d.template_used}`}>
                          Branded template
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : ''}
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </span>
                  </button>
                  {open && (
                    <div className="p-5 bg-white">
                      <DocPdf doc={d} dealId={dealId} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Gate history */}
      {(phase?.history || []).length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1.5">Gate History</div>
          <div className="space-y-1.5">
            {[...phase.history].reverse().map((h, i) => (
              <div key={i} className="text-xs text-slate-500 border-l-2 border-slate-200 pl-3">
                <span className="font-medium text-slate-700">{h.decision}</span> · {h.phase} · {h.decided_by}
                {h.decided_at ? ` · ${new Date(h.decided_at).toLocaleDateString()}` : ''}
                <div className="italic">“{h.rationale}”</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
